import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { 
  School, MapPin, Phone, Save, Upload, Loader2, 
  XCircle, CheckCircle, Calendar, CreditCard, 
  Shield, Clock, DollarSign, AlertCircle, Users,
  ChevronRight, Trash2, Globe
} from 'lucide-react';

const SchoolInfoManagement = ({ db, storage, schoolId, setMessage, setError, userRole }) => {
  const [schoolData, setSchoolData] = useState({
    name: '',
    adresse_ecole: '',
    telephone_ecole: '',
    logoUrl: '',
    subscriptionActive: false,
    subscriptionEndDate: null,
    subscriptionStartDate: null,
    subscriptionAmount: 0,
    subscriptionLastPayment: null,
    appVersion: 'v1'
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savedMessage, setSavedMessage] = useState('');
  
  const [subscriptionAction, setSubscriptionAction] = useState(null);
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [subscriptionAmount, setSubscriptionAmount] = useState(10000);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);

  const clearMessages = () => {
    setMessage('');
    setError('');
    setSavedMessage('');
  };

  useEffect(() => {
    const fetchSchoolInfo = async () => {
      if (!schoolId) {
        setError("ID de l'école manquant.");
        setLoading(false);
        return;
      }

      try {
        const docSnap = await getDoc(doc(db, 'schools', schoolId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSchoolData(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        setError(`Erreur de chargement: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolInfo();
  }, [schoolId, db]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(logoFile);
    setLogoPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [logoFile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSchoolData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubscriptionAction = async () => {
    if (!subscriptionAction) return;
    setIsSaving(true);
    clearMessages();

    try {
      const today = Timestamp.now();
      let updates = {};

      if (subscriptionAction === 'activate' || subscriptionAction === 'extend') {
        let baseDate = (subscriptionAction === 'extend' && schoolData.subscriptionEndDate) 
          ? schoolData.subscriptionEndDate.toDate() 
          : new Date();
        
        const newEndDate = new Date(baseDate);
        newEndDate.setDate(newEndDate.getDate() + parseInt(subscriptionDays));

        updates = {
          subscriptionActive: true,
          subscriptionEndDate: Timestamp.fromDate(newEndDate),
          subscriptionAmount: Number(subscriptionAmount),
          subscriptionLastPayment: today,
          ...(subscriptionAction === 'activate' && { subscriptionStartDate: today })
        };
      } else if (subscriptionAction === 'deactivate') {
        updates = { subscriptionActive: false, subscriptionEndDate: null };
      }

      await setDoc(doc(db, 'schools', schoolId), updates, { merge: true });
      setSchoolData(prev => ({ ...prev, ...updates }));
      setSavedMessage("Abonnement mis à jour avec succès !");
      setShowSubscriptionForm(false);
    } catch (err) {
      setError(`Erreur d'abonnement: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    clearMessages();

    try {
      let finalLogoUrl = schoolData.logoUrl;

      if (logoFile) {
        const storageRef = ref(storage, `logos/${schoolId}_${Date.now()}`);
        const uploadTask = await uploadBytesResumable(storageRef, logoFile);
        finalLogoUrl = await getDownloadURL(uploadTask.ref);
      }

      const infoUpdates = {
        name: schoolData.name,
        adresse_ecole: schoolData.adresse_ecole,
        telephone_ecole: schoolData.telephone_ecole,
        logoUrl: finalLogoUrl,
        appVersion: schoolData.appVersion
      };

      await setDoc(doc(db, 'schools', schoolId), infoUpdates, { merge: true });
      setSchoolData(prev => ({ ...prev, logoUrl: finalLogoUrl }));
      setLogoFile(null);
      setSavedMessage("Informations enregistrées !");
    } catch (err) {
      setError(`Erreur d'enregistrement: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (ts) => ts ? ts.toDate().toLocaleDateString('fr-FR') : '--/--/--';
  const calculateDaysRemaining = () => {
    if (!schoolData.subscriptionEndDate || !schoolData.subscriptionActive) return 0;
    const diff = schoolData.subscriptionEndDate.toDate() - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <Loader2 className="animate-spin text-indigo-600" size={48} />
      <p className="text-slate-500 font-medium animate-pulse">Chargement de l'établissement...</p>
    </div>
  );

  const daysRemaining = calculateDaysRemaining();
  const isSubscribed = schoolData.subscriptionActive && daysRemaining > 0;

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center space-x-4">
          <div className="bg-indigo-100 p-3 rounded-2xl">
            <School className="text-indigo-600" size={32} />
          </div>
          <div>
            <h3 className="text-2xl font-black text-slate-800">Paramètres</h3>
            <p className="text-slate-500 text-sm font-medium">{schoolData.name || 'Configuration de l\'école'}</p>
          </div>
        </div>
        
        <div className={`flex items-center px-4 py-2 rounded-xl text-sm font-bold shadow-sm ${
          isSubscribed ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
        }`}>
          <div className={`w-2 h-2 rounded-full mr-2 animate-pulse ${isSubscribed ? 'bg-emerald-500' : 'bg-rose-500'}`} />
          {isSubscribed ? `Abonnement Actif (${daysRemaining}j)` : 'Abonnement Inactif'}
        </div>
      </div>

      {savedMessage && (
        <div className="bg-emerald-500 text-white p-4 rounded-2xl flex items-center shadow-lg shadow-emerald-200 animate-in fade-in slide-in-from-top-4">
          <CheckCircle size={20} className="mr-3" />
          <span className="font-bold">{savedMessage}</span>
        </div>
      )}

      {/* SUPER ADMIN: GESTION ABONNEMENT */}
      {userRole === 'superAdmin' && (
        <section className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Shield size={120} />
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-8">
              <div className="bg-indigo-500 p-2 rounded-lg">
                <Shield size={20} />
              </div>
              <h4 className="text-xl font-bold tracking-tight">Console d'Abonnement</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/5 border border-white/10 p-5 rounded-2xl backdrop-blur-md">
                <p className="text-indigo-300 text-xs font-black uppercase mb-2">Validité</p>
                <p className="text-2xl font-bold">{formatDate(schoolData.subscriptionEndDate)}</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-5 rounded-2xl backdrop-blur-md">
                <p className="text-indigo-300 text-xs font-black uppercase mb-2">Dernier Paiement</p>
                <p className="text-2xl font-bold">{schoolData.subscriptionAmount?.toLocaleString()} F</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-5 rounded-2xl backdrop-blur-md">
                <p className="text-indigo-300 text-xs font-black uppercase mb-2">Version App</p>
                <p className="text-2xl font-bold uppercase">{schoolData.appVersion}</p>
              </div>
            </div>

            {!showSubscriptionForm ? (
              <div className="flex flex-wrap gap-4">
                <button onClick={() => { setSubscriptionAction('activate'); setShowSubscriptionForm(true); }} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-bold transition-all flex items-center shadow-lg shadow-indigo-900/20">
                  <CheckCircle size={18} className="mr-2" /> Activer / Renouveler
                </button>
                {isSubscribed && (
                  <button onClick={() => { setSubscriptionAction('deactivate'); setShowSubscriptionForm(true); }} className="px-6 py-3 bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 border border-rose-600/30 rounded-xl font-bold transition-all">
                    Désactiver l'accès
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white text-slate-900 p-6 rounded-3xl animate-in zoom-in-95 duration-200">
                <h5 className="font-black text-lg mb-4 flex items-center">
                  {subscriptionAction === 'deactivate' ? <XCircle className="text-rose-500 mr-2" /> : <Calendar className="text-indigo-600 mr-2" />}
                  {subscriptionAction === 'deactivate' ? 'Confirmer la désactivation' : 'Paramètres de l\'abonnement'}
                </h5>

                {subscriptionAction !== 'deactivate' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Jours à ajouter</label>
                      <input type="number" value={subscriptionDays} onChange={(e) => setSubscriptionDays(e.target.value)} className="w-full p-4 bg-slate-100 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 font-bold" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Montant (FCFA)</label>
                      <input type="number" value={subscriptionAmount} onChange={(e) => setSubscriptionAmount(e.target.value)} className="w-full p-4 bg-slate-100 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 font-bold" />
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 mb-6 bg-rose-50 p-4 rounded-2xl border border-rose-100 font-medium">
                    Cette action coupera immédiatement l'accès à tous les utilisateurs de cet établissement.
                  </p>
                )}

                <div className="flex gap-3">
                  <button onClick={handleSubscriptionAction} disabled={isSaving} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-slate-800 transition-all flex items-center justify-center">
                    {isSaving ? <Loader2 className="animate-spin mr-2" /> : <Save size={20} className="mr-2" />}
                    Confirmer l'action
                  </button>
                  <button onClick={() => setShowSubscriptionForm(false)} className="px-8 py-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all">Annuler</button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* INFORMATIONS GENERALES */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Formulaire */}
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSaveInfo} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 space-y-6">
            <h4 className="text-xl font-black text-slate-800 flex items-center mb-6">
              <Globe size={22} className="mr-2 text-indigo-600" /> Profil de l'Établissement
            </h4>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Nom de l'école</label>
              <input name="name" value={schoolData.name} onChange={handleChange} required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-semibold" placeholder="Ex: Groupe Scolaire..." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Adresse</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input name="adresse_ecole" value={schoolData.adresse_ecole} onChange={handleChange} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-semibold" placeholder="Quartier, Ville..." />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input name="telephone_ecole" value={schoolData.telephone_ecole} onChange={handleChange} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-semibold" placeholder="+221 ..." />
                </div>
              </div>
            </div>

            {userRole === 'superAdmin' && (
              <div className="space-y-4 pt-4">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Version Logicielle</label>
                <div className="grid grid-cols-3 gap-4">
                  {['v1', 'v2', 'v3'].map((v) => (
                    <button key={v} type="button" onClick={() => setSchoolData(p => ({...p, appVersion: v}))} className={`py-3 rounded-xl font-black transition-all border-2 ${schoolData.appVersion === v ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300'}`}>
                      {v.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button type="submit" disabled={isSaving} className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-lg shadow-xl shadow-indigo-200 transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-50">
              {isSaving ? <Loader2 className="animate-spin mr-2" /> : <Save className="mr-2" />}
              Enregistrer les modifications
            </button>
          </form>
        </div>

        {/* LOGO UPLOAD */}
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col items-center text-center">
            <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Logo Officiel</h4>
            
            <div className="relative group mb-6">
              <div className="w-48 h-48 rounded-[2.5rem] bg-slate-50 border-4 border-slate-100 overflow-hidden flex items-center justify-center shadow-inner group-hover:border-indigo-100 transition-all">
                {logoPreview || schoolData.logoUrl ? (
                  <img src={logoPreview || schoolData.logoUrl} alt="Logo" className="w-full h-full object-contain p-4" />
                ) : (
                  <School size={60} className="text-slate-200" />
                )}
              </div>
              <label className="absolute bottom-2 right-2 bg-indigo-600 p-3 rounded-2xl text-white cursor-pointer hover:bg-indigo-700 shadow-lg transition-transform hover:scale-110 active:scale-90">
                <Upload size={20} />
                <input type="file" className="hidden" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} />
              </label>
            </div>

            <p className="text-xs text-slate-400 font-medium leading-relaxed px-4">
              Recommandé : Format carré PNG ou JPG, fond transparent ou blanc (max 2Mo).
            </p>

            {logoFile && (
              <div className="mt-4 flex items-center bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100">
                <span className="text-xs font-bold text-indigo-600 truncate max-w-[120px]">{logoFile.name}</span>
                <button onClick={() => setLogoFile(null)} className="ml-2 text-indigo-400 hover:text-rose-500"><XCircle size={14} /></button>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-[2.5rem] border border-indigo-100">
            <h5 className="font-bold text-indigo-900 text-sm mb-4 flex items-center">
              <AlertCircle size={16} className="mr-2" /> Aide & Support
            </h5>
            <p className="text-xs text-indigo-800/60 leading-relaxed font-medium">
              Besoin d'aide pour configurer votre établissement ou pour changer de version ? Contactez notre support technique disponible 24/7.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolInfoManagement;